/*******************************************************************************
 * Licensed Materials - Property of IBM
 * (c) Copyright IBM Corporation 2017, 2023. All Rights Reserved.
 *
 * Note to U.S. Government Users Restricted Rights:
 * Use, duplication or disclosure restricted by GSA ADP Schedule
 * Contract with IBM Corp.
 *******************************************************************************/
'use strict';

/*
 * URL utility functions.
 */

/*
 * Returns a new url with the given param added.
 */
exports.addUrlParam = function (url, name, value) {
  var queryIdx = url.indexOf('?'),
    hashIdx = url.indexOf('#'),
    hasQuery = queryIdx !== -1,
    hasHash = hashIdx !== -1,
    idx = hasHash ? hashIdx : url.length,
    lastIndexIsQuestionMark = url.charAt(url.length - 1) === '?',
    param = (hasQuery ? lastIndexIsQuestionMark ? '' : '&' : '?') + encodeURIComponent(name).replace(/%3A/g, ':') + (value ? '=' + encodeURIComponent(value).replace(/%3A/g, ':') : '');
  return url.substring(0, idx) + param + url.substring(idx);
};

/*
 * Returns a new url with all given params added (of form location.search).
 */
exports.addUrlParams = function (url, queryString) {
  var keyValues;
  if (queryString) {
    if (queryString.charAt(0) === '?') {
      keyValues = queryString.substring(1);
    } else {
      keyValues = queryString;
    }
    var paramList = keyValues.split('&');
    for (var i = 0; i < paramList.length; i++) {
      var nameValueArray = paramList[i].split('=');
      if (nameValueArray.length === 2) {
        url = exports.addUrlParam(url, nameValueArray[0], decodeURIComponent(nameValueArray[1]));
      }
    }
  }
  return url;
};

/*
 * Returns the first value of the URL's given param, or undefined if there is none.
 * Returns true if the param has no value.
 */
exports.param = function (url, param) {
  var p = exports.params(url)[param];
  if (p) {
    return p.length === 0 ? true : p[0];
  }
};

/*
 * Returns the URL params in the form of an object. e.g.
 * https://test.com?param1=value1&param2=value2a&param2=value2b
 * =>
 * {
 *    param1: ['value1'],
 *    param2: ['value2a', 'value2b']
 * }
 */
exports.params = function (url) {
  var params = {};
  var idx = url.indexOf('?');
  if (idx !== -1) {
    var query = url.substring(idx + 1);
    var paramArray = query.length > 0 ? query.split('&') : [];
    for (var i = 0, len = paramArray.length; i < len; ++i) {
      var p = paramArray[i].split('='),
        name = decodeURIComponent(p[0]),
        arr = params[name];
      if (!arr) {
        arr = params[name] = [];
      }
      p[1] && arr.push(decodeURIComponent(p[1]));
    }
  }
  return params;
};

/*
 * Returns query string params in the form of a string if given an object. e.g.
 * {
 *    param1: 'value1',
 *    param2: ['value2a', 'value2b']
 * }
 * =>
 * ?param1=value1&param2=value2a&param2=value2b
 */
exports.objToQueryString = function (queryStringObj) {
  var queryStringBuildUp = '';
  for (var _i = 0, _Object$keys = Object.keys(queryStringObj); _i < _Object$keys.length; _i++) {
    var queryParam = _Object$keys[_i];
    if (queryStringObj.hasOwnProperty(queryParam)) {
      if (queryStringBuildUp !== '') {
        queryStringBuildUp += '&';
      }
      var value = queryStringObj[queryParam];

      // special case: multiple params with same key.
      if (Array.isArray(value)) {
        var firstInArray = true;
        for (var _i2 = 0, _Object$keys2 = Object.keys(value); _i2 < _Object$keys2.length; _i2++) {
          var innerIndex = _Object$keys2[_i2];
          if (!firstInArray) {
            queryStringBuildUp += '&';
          } else {
            firstInArray = false;
          }
          queryStringBuildUp += encodeURIComponent(queryParam) + '=' + encodeURIComponent(value[innerIndex]);
        }
      } else {
        queryStringBuildUp += encodeURIComponent(queryParam) + '=' + encodeURIComponent(queryStringObj[queryParam]);
      }
    }
  }
  if (queryStringBuildUp !== '') {
    queryStringBuildUp = '?' + queryStringBuildUp;
  }
  return queryStringBuildUp;
};

/*
 * Removes the given param from the URL, without reloading the page. This is useful for cases where you
 * pass along a param to inform the next page about a flow, but don't want that URL param to permanently appear
 * in any links or bookmarks.
 */
exports.removeUrlParam = function (param) {
  var search = location.search;
  if (search && history.replaceState) {
    var params = search.substring(1).split('&'),
      found = false;
    for (var i = 0, len = params.length; i < len; ++i) {
      var p = params[i];
      var name = p.split('=')[0];
      if (name === param) {
        params.splice(i, 1);
        found = true;
        break;
      }
    }
    if (found) {
      var newSearch = (params.length ? '?' : '') + params.join('&');
      history.replaceState({}, null, location.pathname + newSearch);
    }
  }
};

/*
 * Fixes dashboard_urls for those integrations that may need an env_id (e.g. pipeline, private_worker)
 */
exports.fixDashboardUrl = function (dashboard_url, service_id, env_id) {
  // Pipeline / private_worker integrations are the only ones that need env ids
  if (service_id === 'pipeline' || service_id === 'private_worker') {
    // only add env_id if not present on the dashboard_url
    if (!exports.params(dashboard_url).env_id) {
      return exports.addUrlParam(dashboard_url, 'env_id', env_id);
    }
  }
  return dashboard_url;
};